import React, { useEffect, useState, forwardRef } from "react";
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
    TableContainer,
    useTheme,
    Chip,
    CircularProgress
} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

// Utility to fix float precision (assuming this is globally available)
const safeRound = (num) => parseFloat(num.toFixed(2));

// const CHART_COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#e91e63", "#00bcd4"];


// --- Data Processing Utilities ---
const processItemSalesData = (allOrders) => {

    if (!allOrders || !Array.isArray(allOrders) || allOrders.length === 0) {
        return { totalUnits: 0, totalRevenue: 0, salesTable: [], top10Chart: [] };
    }


    const itemMap = {}; // { itemName: { qty: 0, revenue: 0 } }
    let totalUnitsSold = 0;
    let totalRevenue = 0;

    // Filter and loop only through COMPLETED orders (oStatus === 2)
    const completedOrders = allOrders.filter(order => order.oStatus === 2);

    completedOrders.forEach(order => {
        // order.orders is an array of sub-orders, each containing an array of items
        order.orders?.forEach(subOrder => {
            subOrder.order?.forEach(item => {
                const name = item.item;
                const quantity = item.quantity ?? 0;
                const price = item.price ?? 0;
                const revenue = price * quantity;

                if (!itemMap[name]) {
                    itemMap[name] = { item: name, qty: 0, revenue: 0 };
                }

                itemMap[name].qty += quantity;
                itemMap[name].revenue += revenue;
                totalUnitsSold += quantity;
                totalRevenue += revenue;
            });
        });
    });

    // Convert map to array and round final totals
    const salesTable = Object.values(itemMap).map(item => ({
        ...item,
        revenue: safeRound(item.revenue),
        qty: item.qty
    })).sort((a, b) => b.revenue - a.revenue);

    // Prepare top 10 data for chart
    const top10Chart = salesTable
        .slice(0, 10)
        .map(item => ({
            name: item.item.length > 15 ? item.item.substring(0, 12) + '...' : item.item, // Shorten name for chart axis
            Units: item.qty
        }));

    return {
        totalUnits: totalUnitsSold,
        totalRevenue: safeRound(totalRevenue),
        salesTable,
        top10Chart
    };
};

// --- Main Component ---
export const ItemSalesReportPage = forwardRef(({ rawData }, ref) => {
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        let ordersArray = rawData?.orders || [];

        // --- Session Storage Fallback ---
        if (ordersArray.length === 0) {

            const stored = sessionStorage.getItem("itemSaleReportData"); // Use dedicated key
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    ordersArray = parsed.rawData?.orders || [];
                } catch (e) { console.error("Error parsing stored data:", e); }
            }
        }

        if (ordersArray.length > 0) {

            const processedData = processItemSalesData(ordersArray);
            setReportData(processedData);
        } else {
            setReportData({ totalUnits: 0, totalRevenue: 0, salesTable: [], top10Chart: [] });
        }
        setIsLoading(false);
    }, [rawData]);

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;

    if (!reportData || reportData.salesTable.length === 0) {
        return (
            <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }} ref={ref}>
                <Typography variant="h6" color="text.secondary">
                    No completed sales data found for this period.
                </Typography>
            </Paper>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>

            {/* 1. Summary Cards Section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: '#f5faff' }}>
                <Typography variant="h6" color="primary.dark" gutterBottom>Overall Metrics</Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Chip label={`Total Units Sold: ${reportData.totalUnits}`} color="primary" variant="filled" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
                    <Chip label={`Total Item Revenue: $${reportData.totalRevenue.toFixed(2)}`} color="secondary" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
                </Box>
            </Paper>

            {/* 2. Top 10 Items Graph */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: '#fff3e0' }}>
                <Typography variant="h6" color="warning.main" gutterBottom>
                    Top 10 Selling Items (By Quantity)
                </Typography>
                <Box sx={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData.top10Chart} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" label={{ value: 'Units Sold', position: 'insideBottom', offset: -5 }} />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="Units" fill="#ed6c02" name="Units Sold" />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>

            {/* 3. Detailed Item Sales Table */}
            <Paper elevation={3} sx={{ p: 3, backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" color="info.main" gutterBottom>
                    Detailed Item Sales Log
                </Typography>
                <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#bbdefb' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Qty Sold</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Revenue ($)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.salesTable.map((row, index) => (
                                <TableRow
                                    key={row.item}
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                        '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' }
                                    }}
                                >
                                    <TableCell>{row.item}</TableCell>
                                    <TableCell align="right">{row.qty}</TableCell>
                                    <TableCell align="right">${row.revenue.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
});